import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  ArrowLeft, Sparkles, FileText, Trash2, Plus, X,
  Stethoscope, Pill, AlertTriangle, CalendarCheck, StickyNote, User, Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import {
  getPatient, deletePatient, generateSummary,
  addDiagnosis, deleteDiagnosis,
  addMedication, deleteMedication,
  addRiskAlert, deleteRiskAlert,
  addFollowUp, deleteFollowUp,
  addNote, deleteNote,
} from "@/lib/api";

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [dialog, setDialog] = useState(null); // 'diagnosis' | 'medication' | 'risk' | 'followup' | 'note'
  const [formData, setFormData] = useState({});

  const fetchPatient = useCallback(async () => {
    try {
      const res = await getPatient(id);
      setPatient(res.data);
    } catch {
      toast.error("Failed to load patient");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchPatient(); }, [fetchPatient]);

  const handleGenerateSummary = async () => {
    setGenerating(true);
    try {
      await generateSummary(id);
      toast.success("Discharge summary generated");
      fetchPatient();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate summary");
    } finally {
      setGenerating(false);
    }
  };

  const handleDeletePatient = async () => {
    if (!window.confirm("Delete this patient? This action cannot be undone.")) return;
    try {
      await deletePatient(id);
      toast.success("Patient deleted");
      navigate("/");
    } catch {
      toast.error("Failed to delete patient");
    }
  };

  const handleAddRecord = async () => {
    try {
      if (dialog === "diagnosis") await addDiagnosis(id, formData);
      else if (dialog === "medication") await addMedication(id, formData);
      else if (dialog === "risk") await addRiskAlert(id, formData);
      else if (dialog === "followup") await addFollowUp(id, formData);
      else if (dialog === "note") await addNote(id, formData);
      toast.success("Record added");
      setDialog(null);
      setFormData({});
      fetchPatient();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add record");
    }
  };

  const handleDeleteRecord = async (type, recordId) => {
    try {
      if (type === "diagnosis") await deleteDiagnosis(id, recordId);
      else if (type === "medication") await deleteMedication(id, recordId);
      else if (type === "risk") await deleteRiskAlert(id, recordId);
      else if (type === "followup") await deleteFollowUp(id, recordId);
      else if (type === "note") await deleteNote(id, recordId);
      toast.success("Record removed");
      fetchPatient();
    } catch {
      toast.error("Failed to delete record");
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse" data-testid="patient-loading">
        <div className="h-8 bg-slate-200 rounded w-1/3" />
        <div className="h-40 bg-slate-100 rounded-xl" />
        <div className="h-60 bg-slate-100 rounded-xl" />
      </div>
    );
  }

  if (!patient) return null;

  const p = patient;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="patient-detail-page">
      {/* Header */}
      <div className="flex items-start gap-4 flex-wrap">
        <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 tracking-tight" data-testid="patient-name">
              {p.first_name} {p.last_name}
            </h1>
            <Badge
              className={
                p.status === "admitted"
                  ? "border-brand-200 bg-brand-50 text-brand-700 rounded-full"
                  : "border-emerald-200 bg-emerald-50 text-emerald-700 rounded-full"
              }
              variant="outline"
              data-testid="patient-status-badge"
            >
              {p.status}
            </Badge>
          </div>
          <p className="text-sm text-slate-500 mt-1 font-mono" data-testid="patient-mir">MIR-{p.mir_number}</p>
        </div>
        <div className="flex gap-2">
          {p.summary && (
            <Button variant="outline" asChild data-testid="view-summary-btn">
              <Link to={`/patients/${id}/summary`}>
                <FileText className="w-4 h-4 mr-2" />
                View Summary
              </Link>
            </Button>
          )}
          <Button
            onClick={handleGenerateSummary}
            disabled={generating}
            className="bg-brand-600 text-white hover:bg-brand-700 shadow-sm"
            data-testid="generate-summary-btn"
          >
            <Sparkles className={`w-4 h-4 mr-2 ${generating ? "animate-spin" : ""}`} />
            {generating ? "Generating..." : p.summary ? "Regenerate Summary" : "Generate Summary"}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" data-testid="patient-actions-menu">
                <span className="text-lg leading-none">...</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="text-red-600" onClick={handleDeletePatient} data-testid="delete-patient-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Patient
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="patient-info-cards">
        <InfoCard icon={User} label="Gender" value={p.gender} />
        <InfoCard icon={CalendarCheck} label="DOB" value={format(new Date(p.date_of_birth), "MMM d, yyyy")} />
        <InfoCard icon={Clock} label="Admitted" value={format(new Date(p.admission_date), "MMM d, yyyy")} />
        <InfoCard icon={Stethoscope} label="Physician" value={p.attending_physician || "Not assigned"} />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="diagnoses" className="space-y-4" data-testid="clinical-tabs">
        <TabsList className="bg-slate-100" data-testid="tabs-list">
          <TabsTrigger value="diagnoses" data-testid="tab-diagnoses">
            <Stethoscope className="w-3.5 h-3.5 mr-1.5" />Diagnoses ({p.diagnoses.length})
          </TabsTrigger>
          <TabsTrigger value="medications" data-testid="tab-medications">
            <Pill className="w-3.5 h-3.5 mr-1.5" />Medications ({p.medications.length})
          </TabsTrigger>
          <TabsTrigger value="risks" data-testid="tab-risks">
            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />Alerts ({p.risk_alerts.length})
          </TabsTrigger>
          <TabsTrigger value="followups" data-testid="tab-followups">
            <CalendarCheck className="w-3.5 h-3.5 mr-1.5" />Follow-ups ({p.follow_ups.length})
          </TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">
            <StickyNote className="w-3.5 h-3.5 mr-1.5" />Notes ({p.notes.length})
          </TabsTrigger>
        </TabsList>

        {/* Diagnoses */}
        <TabsContent value="diagnoses" data-testid="diagnoses-content">
          <RecordSection
            title="Diagnoses"
            onAdd={() => { setDialog("diagnosis"); setFormData({ code: "", description: "", diagnosis_type: "secondary" }); }}
            empty={p.diagnoses.length === 0}
          >
            {p.diagnoses.map((d) => (
              <RecordCard key={d.id} onDelete={() => handleDeleteRecord("diagnosis", d.id)} testId={`diagnosis-${d.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  {d.code && <Badge variant="outline" className="text-xs font-mono">{d.code}</Badge>}
                  <Badge className={d.diagnosis_type === "primary" ? "bg-brand-50 text-brand-700 border-brand-200 rounded-full text-xs" : "bg-slate-50 text-slate-600 border-slate-200 rounded-full text-xs"} variant="outline">
                    {d.diagnosis_type}
                  </Badge>
                </div>
                <p className="text-sm text-slate-800 font-medium">{d.description}</p>
                {d.diagnosed_date && <p className="text-xs text-slate-500 mt-1">Diagnosed: {format(new Date(d.diagnosed_date), "MMM d, yyyy")}</p>}
              </RecordCard>
            ))}
          </RecordSection>
        </TabsContent>

        {/* Medications */}
        <TabsContent value="medications" data-testid="medications-content">
          <RecordSection
            title="Medications"
            onAdd={() => { setDialog("medication"); setFormData({ name: "", dosage: "", frequency: "", route: "oral", instructions: "" }); }}
            empty={p.medications.length === 0}
          >
            {p.medications.map((m) => (
              <RecordCard key={m.id} onDelete={() => handleDeleteRecord("medication", m.id)} testId={`medication-${m.id}`}>
                <p className="text-sm text-slate-800 font-medium">{m.name}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                  {m.dosage && <span>{m.dosage}</span>}
                  {m.frequency && <span>{m.frequency}</span>}
                  {m.route && <span>via {m.route}</span>}
                </div>
                {m.instructions && <p className="text-xs text-slate-500 mt-1 italic">{m.instructions}</p>}
              </RecordCard>
            ))}
          </RecordSection>
        </TabsContent>

        {/* Risk Alerts */}
        <TabsContent value="risks" data-testid="risks-content">
          <RecordSection
            title="Risk Alerts"
            onAdd={() => { setDialog("risk"); setFormData({ alert_type: "", severity: "medium", description: "" }); }}
            empty={p.risk_alerts.length === 0}
          >
            {p.risk_alerts.map((r) => (
              <RecordCard key={r.id} onDelete={() => handleDeleteRecord("risk", r.id)} testId={`risk-${r.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-slate-800">{r.alert_type}</span>
                  <Badge className={
                    r.severity === "critical" ? "bg-red-50 text-red-700 border-red-200 rounded-full text-xs" :
                    r.severity === "high" ? "bg-amber-50 text-amber-700 border-amber-200 rounded-full text-xs" :
                    "bg-slate-50 text-slate-600 border-slate-200 rounded-full text-xs"
                  } variant="outline">{r.severity}</Badge>
                </div>
                <p className="text-xs text-slate-600">{r.description}</p>
              </RecordCard>
            ))}
          </RecordSection>
        </TabsContent>

        {/* Follow-ups */}
        <TabsContent value="followups" data-testid="followups-content">
          <RecordSection
            title="Follow-up Appointments"
            onAdd={() => { setDialog("followup"); setFormData({ department: "", physician: "", scheduled_date: "", instructions: "" }); }}
            empty={p.follow_ups.length === 0}
          >
            {p.follow_ups.map((f) => (
              <RecordCard key={f.id} onDelete={() => handleDeleteRecord("followup", f.id)} testId={`followup-${f.id}`}>
                <p className="text-sm text-slate-800 font-medium">{f.department || "Department TBD"}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                  {f.physician && <span>with {f.physician}</span>}
                  {f.scheduled_date && <span>on {format(new Date(f.scheduled_date), "MMM d, yyyy")}</span>}
                </div>
                {f.instructions && <p className="text-xs text-slate-500 mt-1 italic">{f.instructions}</p>}
              </RecordCard>
            ))}
          </RecordSection>
        </TabsContent>

        {/* Notes */}
        <TabsContent value="notes" data-testid="notes-content">
          <RecordSection
            title="Clinical Notes"
            onAdd={() => { setDialog("note"); setFormData({ author: "", content: "", note_type: "progress" }); }}
            empty={p.notes.length === 0}
          >
            {p.notes.map((n) => (
              <RecordCard key={n.id} onDelete={() => handleDeleteRecord("note", n.id)} testId={`note-${n.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-xs rounded-full">{n.note_type}</Badge>
                  {n.author && <span className="text-xs text-slate-500">by {n.author}</span>}
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{n.content}</p>
                {n.created_at && <p className="text-xs text-slate-400 mt-1">{format(new Date(n.created_at), "MMM d, yyyy 'at' h:mm a")}</p>}
              </RecordCard>
            ))}
          </RecordSection>
        </TabsContent>
      </Tabs>

      {/* Add Record Dialogs */}
      <Dialog open={!!dialog} onOpenChange={(open) => { if (!open) { setDialog(null); setFormData({}); } }}>
        <DialogContent data-testid="add-record-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Add {dialog === "diagnosis" ? "Diagnosis" : dialog === "medication" ? "Medication" : dialog === "risk" ? "Risk Alert" : dialog === "followup" ? "Follow-up" : "Note"}
            </DialogTitle>
            <DialogDescription>Fill in the details below to add a new clinical record.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {dialog === "diagnosis" && (
              <>
                <Input placeholder="ICD Code (optional)" value={formData.code || ""} onChange={(e) => setFormData({ ...formData, code: e.target.value })} data-testid="input-diagnosis-code" />
                <Input placeholder="Description *" value={formData.description || ""} onChange={(e) => setFormData({ ...formData, description: e.target.value })} data-testid="input-diagnosis-desc" />
                <Select value={formData.diagnosis_type} onValueChange={(v) => setFormData({ ...formData, diagnosis_type: v })}>
                  <SelectTrigger data-testid="select-diagnosis-type"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Primary</SelectItem>
                    <SelectItem value="secondary">Secondary</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
            {dialog === "medication" && (
              <>
                <Input placeholder="Medication name *" value={formData.name || ""} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="input-med-name" />
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Dosage" value={formData.dosage || ""} onChange={(e) => setFormData({ ...formData, dosage: e.target.value })} data-testid="input-med-dosage" />
                  <Input placeholder="Frequency" value={formData.frequency || ""} onChange={(e) => setFormData({ ...formData, frequency: e.target.value })} data-testid="input-med-frequency" />
                </div>
                <Select value={formData.route || "oral"} onValueChange={(v) => setFormData({ ...formData, route: v })}>
                  <SelectTrigger data-testid="select-med-route"><SelectValue placeholder="Route" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="oral">Oral</SelectItem>
                    <SelectItem value="IV">IV</SelectItem>
                    <SelectItem value="IM">IM</SelectItem>
                    <SelectItem value="topical">Topical</SelectItem>
                    <SelectItem value="subcutaneous">Subcutaneous</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea placeholder="Special instructions" value={formData.instructions || ""} onChange={(e) => setFormData({ ...formData, instructions: e.target.value })} data-testid="input-med-instructions" />
              </>
            )}
            {dialog === "risk" && (
              <>
                <Input placeholder="Alert type *" value={formData.alert_type || ""} onChange={(e) => setFormData({ ...formData, alert_type: e.target.value })} data-testid="input-risk-type" />
                <Select value={formData.severity || "medium"} onValueChange={(v) => setFormData({ ...formData, severity: v })}>
                  <SelectTrigger data-testid="select-risk-severity"><SelectValue placeholder="Severity" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea placeholder="Description *" value={formData.description || ""} onChange={(e) => setFormData({ ...formData, description: e.target.value })} data-testid="input-risk-desc" />
              </>
            )}
            {dialog === "followup" && (
              <>
                <Input placeholder="Department" value={formData.department || ""} onChange={(e) => setFormData({ ...formData, department: e.target.value })} data-testid="input-followup-dept" />
                <Input placeholder="Physician" value={formData.physician || ""} onChange={(e) => setFormData({ ...formData, physician: e.target.value })} data-testid="input-followup-physician" />
                <Input type="date" value={formData.scheduled_date || ""} onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })} data-testid="input-followup-date" />
                <Textarea placeholder="Instructions" value={formData.instructions || ""} onChange={(e) => setFormData({ ...formData, instructions: e.target.value })} data-testid="input-followup-instructions" />
              </>
            )}
            {dialog === "note" && (
              <>
                <Input placeholder="Author" value={formData.author || ""} onChange={(e) => setFormData({ ...formData, author: e.target.value })} data-testid="input-note-author" />
                <Select value={formData.note_type || "progress"} onValueChange={(v) => setFormData({ ...formData, note_type: v })}>
                  <SelectTrigger data-testid="select-note-type"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="progress">Progress</SelectItem>
                    <SelectItem value="nursing">Nursing</SelectItem>
                    <SelectItem value="procedure">Procedure</SelectItem>
                    <SelectItem value="discharge">Discharge</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea placeholder="Content *" rows={4} value={formData.content || ""} onChange={(e) => setFormData({ ...formData, content: e.target.value })} data-testid="input-note-content" />
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialog(null); setFormData({}); }} data-testid="dialog-cancel-btn">Cancel</Button>
            <Button onClick={handleAddRecord} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="dialog-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-slate-100 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-slate-400" strokeWidth={1.5} />
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      </div>
      <p className="text-sm font-medium text-slate-800 truncate">{value}</p>
    </div>
  );
}

function RecordSection({ title, onAdd, empty, children }) {
  return (
    <div className="bg-white border border-slate-100 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-slate-800">{title}</h3>
        <Button size="sm" variant="outline" onClick={onAdd} data-testid={`add-${title.toLowerCase().replace(/\s+/g, "-")}-btn`}>
          <Plus className="w-3.5 h-3.5 mr-1" />Add
        </Button>
      </div>
      {empty ? (
        <p className="text-sm text-slate-400 text-center py-6">No {title.toLowerCase()} recorded yet</p>
      ) : (
        <div className="space-y-2">{children}</div>
      )}
    </div>
  );
}

function RecordCard({ children, onDelete, testId }) {
  return (
    <div className="bg-slate-50 border border-slate-100 rounded-lg p-3 group relative" data-testid={testId}>
      {children}
      <button
        onClick={onDelete}
        className="absolute top-2 right-2 p-1 rounded-md opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all"
        data-testid={`delete-${testId}`}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
